/**
 * Lightweight YouTube URL helpers used wherever the app derives a
 * thumbnail from a stored external_url without pinging YouTube's
 * oEmbed API.
 *
 * The public dancer profile (`dancer-videos.tsx`) and the dancer's
 * categorisation grid (`media-categorization-grid.tsx`) both fall back
 * to YouTube's deterministic `i.ytimg.com/vi/<id>/hqdefault.jpg` tile
 * when no cached `thumbnail_url` exists. Vimeo / TikTok get cached
 * thumbnails via `refreshVideoThumbnailsAction`; YouTube has stable
 * predictable URLs so we synthesise them client-side.
 */

/**
 * Extract the 11-char video id from any YouTube URL shape we accept:
 *   - youtu.be/<id>
 *   - youtube.com/watch?v=<id>
 *   - youtube.com/embed/<id>
 *   - youtube.com/shorts/<id>
 *   - youtube.com/live/<id>
 *   - youtube.com/v/<id>
 *
 * Returns `null` for non-YouTube URLs, malformed URLs, or anything
 * else we can't confidently parse.
 */
export function getYouTubeVideoId(url: string | null): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') {
      return parsed.pathname.slice(1) || null;
    }
    if (!host.includes('youtube')) return null;
    const v = parsed.searchParams.get('v');
    if (v) return v;
    const match = parsed.pathname.match(
      /^\/(?:embed|shorts|live|v)\/([^/?#]+)/,
    );
    return match ? match[1]! : null;
  } catch {
    return null;
  }
}

/**
 * Build the `hqdefault.jpg` thumbnail URL for a YouTube video, or
 * `null` if the URL isn't recognisably YouTube. Hits no network — the
 * thumbnail path is deterministic from the video id.
 */
export function getYouTubeThumbnailUrl(url: string | null): string | null {
  const id = getYouTubeVideoId(url);
  return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : null;
}

/**
 * Ordered fallback chain for a YouTube thumbnail:
 *   hqdefault (480×360) → mqdefault (320×180) → default (120×90).
 * `hqdefault` 404s for some restricted/old videos; the lower tiers
 * almost always exist. Empty array for non-YouTube URLs.
 */
export function getYouTubeThumbnailUrlFallbacks(url: string | null): string[] {
  const id = getYouTubeVideoId(url);
  if (!id) return [];
  return [
    `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
    `https://i.ytimg.com/vi/${id}/mqdefault.jpg`,
    `https://i.ytimg.com/vi/${id}/default.jpg`,
  ];
}
